import { useEffect, useMemo, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import { Highlight } from '@tiptap/extension-highlight';
import { Markdown } from '@tiptap/markdown';
import { Marked } from 'marked';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import {
  getOverviewSectionHeader,
  getExecutionStepsSectionHeader,
} from '../constants';
import {
  ParagraphHighlightExtension,
  SelectionHighlightExtension,
  Details,
  Summary,
} from '../extensions';
import { captureException } from '@/modules/sentry';
import { logger } from '@/shared/utils/logger';

// Parse with an isolated marked instance, not the editor's @tiptap/markdown
// manager: that manager mutates the global marked singleton and corrupts list
// parsing (dropped leading characters, broken code spans) for a list item with
// a nested list followed by a paragraph.
const markdownParser = new Marked();

/**
 * Custom Highlight extension that uses data-color attribute without inline styles.
 * This allows CSS to fully control the appearance based on data-color values.
 */
const CustomHighlight = Highlight.extend({
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: element => element.getAttribute('data-color'),
        renderHTML: attributes => {
          if (!attributes.color) {
            return {};
          }
          // Only output data-color, no inline styles
          // This allows CSS to control appearance based on data-color value
          return {
            'data-color': attributes.color,
          };
        },
      },
    };
  },
});

interface IUseAutomationEditorProps {
  aopCode?: string;
  description?: string;
  /**
   * Whether to wrap the content in "Overview" / "Execution Steps" section
   * headers. Quill V2 automations embed the overview directly in the AOP, so
   * the AOP is rendered as-is without these headers. Defaults to true.
   */
  includeSectionHeaders?: boolean;
  /**
   * Whether the editor accepts direct typing (SOP preview mode). Defaults to
   * false — every other consumer (published/built automations, diff view)
   * stays read-only.
   */
  editable?: boolean;
  /**
   * Fires with the editor's current markdown whenever the user edits content
   * directly (never for programmatic `setContent` calls driven by
   * `aopCode`/`description` changing). Callers debounce their own
   * persistence on top of this.
   */
  onMarkdownChange?: (markdown: string) => void;
}

/**
 * Builds the combined markdown content with Overview and Execution Steps sections.
 * @param description - The automation description (shown in Overview)
 * @param aopCode - The AOP code (shown in Execution Steps)
 * @param includeSectionHeaders - Whether to prepend the Overview/Execution Steps
 * @returns Combined markdown content
 */
export function buildCombinedContent(
  description?: string,
  aopCode?: string,
  includeSectionHeaders = true,
): string {
  // Quill V2: the overview lives inside the AOP itself, so render the AOP code
  // verbatim without section headers or a separate description block.
  if (!includeSectionHeaders) {
    return aopCode && aopCode.trim() !== '' ? aopCode : '';
  }

  const parts: string[] = [];

  if (description && description.trim() !== '') {
    parts.push(getOverviewSectionHeader());
    parts.push(description);
  }

  if (aopCode && aopCode.trim() !== '') {
    parts.push(getExecutionStepsSectionHeader());
    parts.push(aopCode);
  }

  return parts.join('\n');
}

/**
 * Custom hook to initialize and manage the TipTap editor for automation code.
 * Handles only markdown content format. If not markdown renders as text.
 * Automatically updates the editor content when AOP code or description changes.
 * Displays description under Overview section and AOP code under Execution Steps section.
 */
export function useAutomationEditor({
  aopCode,
  description,
  includeSectionHeaders = true,
  editable = false,
  onMarkdownChange,
}: IUseAutomationEditorProps) {
  // Build combined markdown content with Overview and Execution Steps sections
  const combinedContent = useMemo(
    () => buildCombinedContent(description, aopCode, includeSectionHeaders),
    [description, aopCode, includeSectionHeaders],
  );

  // Bridges the latest callback into the stable `onUpdate` closure below
  // without recreating the editor instance every render.
  const onMarkdownChangeRef = useRef(onMarkdownChange);
  onMarkdownChangeRef.current = onMarkdownChange;

  // The markdown last written into the editor programmatically (initial server
  // content or a save round-trip echo). `onUpdate` compares against this to
  // distinguish a genuine user edit from a programmatic `setContent`, instead
  // of relying on a fragile microtask-timing guard: if `onUpdate` ever fired a
  // tick after the guard reset (async transactions, StrictMode double-effects,
  // editable flips), the programmatic content got re-saved as if the user typed
  // it — the feedback loop that was clobbering the preview.md artifact.
  const lastSyncedMarkdownRef = useRef<string | null>(null);

  // Initialize editor with empty content to avoid parsing errors during initial render.
  // Content is set via useEffect with proper error handling.
  const editor = useEditor({
    extensions: [
      Markdown,
      StarterKit.configure({
        // `dir="auto"` on every text block, so an automation authored in an RTL
        // language (Arabic, Hebrew, …) reads right-to-left without the app
        // knowing anything about its language. The browser resolves each block
        // from its own first strong directional character, which is why this
        // sits on the blocks and not on the editor container: the container
        // would resolve once, from whichever block happens to come first — the
        // injected English "Overview" header, in the `includeSectionHeaders`
        // case — and pin the whole document to that direction. Per block, an
        // English heading stays LTR while the RTL steps under it flip.
        paragraph: {
          HTMLAttributes: {
            dir: 'auto',
          },
        },
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
          HTMLAttributes: {
            class: 'markdown-heading',
            dir: 'auto',
          },
        },
        bold: {
          HTMLAttributes: {
            class: 'font-semibold',
          },
        },
        italic: {
          HTMLAttributes: {
            class: 'italic',
          },
        },
        strike: {
          HTMLAttributes: {
            class: 'line-through',
          },
        },
        code: {
          HTMLAttributes: {
            class: 'markdown-inline-code',
          },
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'markdown-code-block',
            dir: 'auto',
          },
        },
        blockquote: {
          HTMLAttributes: {
            class: 'markdown-blockquote',
            dir: 'auto',
          },
        },
        horizontalRule: {
          HTMLAttributes: {
            class: 'markdown-hr',
          },
        },
        listItem: {
          HTMLAttributes: {
            class: 'list-item',
            dir: 'auto',
          },
        },
        // Logical `ms-`/`ps-` instead of `ml-`/`pl-`: identical in an LTR
        // list, but an RTL one indents (and hangs its markers) on the right.
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal list-outside ms-4 ps-2 my-1',
            dir: 'auto',
          },
        },
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc list-outside ms-4 ps-2 my-1',
            dir: 'auto',
          },
        },
      }),
      CustomHighlight.configure({ multicolor: true }),
      ParagraphHighlightExtension,
      SelectionHighlightExtension,
      Details,
      Summary,
      Table.configure({
        resizable: false,
        renderWrapper: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder: 'Loading automation code...',
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: '', // Start with empty content; set via useEffect with error handling
    editable,
    editorProps: {
      attributes: {
        class:
          'w-full h-full min-h-full p-8 xl:p-12 focus:outline-none focus:ring-0 tiptap-editor-container',
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      const markdown = updatedEditor.getMarkdown();
      // Ignore updates that merely echo the content we just synced in
      // programmatically (initial load or a save round-trip). Only a genuine
      // user edit diverges from the last synced markdown.
      if (markdown === lastSyncedMarkdownRef.current) return;
      onMarkdownChangeRef.current?.(markdown);
    },
  });

  // Flip editability without recreating the editor (which would drop undo
  // history / cursor position mid-session, e.g. right when "Make
  // deterministic" completes and the pane goes read-only).
  // `setEditable` defaults its second arg (`emitUpdate`) to `true` — it fires
  // a real "update" event (reading whatever the doc currently contains) on
  // every flip, exactly like a keystroke would. Since this effect runs
  // *before* the content-sync effect below on the same render (declaration
  // order), toggling editable can fire `onUpdate` a beat before the doc has
  // been synced to the new content, reading stale/empty content and
  // debounce-saving it over whatever was just seeded. Passing `false` here
  // keeps every non-typing editor mutation in this hook emit-free.
  // eslint-disable-next-line no-restricted-syntax -- reason: Imperative TipTap API call to sync editable state
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable, false);
  }, [editor, editable]);

  // Update editor content when combinedContent changes (description or aopCode)
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync editor content when AOP code or description changes externally
  useEffect(() => {
    if (!editor) {
      return;
    }

    // In editable (preview) mode, don't clobber content the user is actively
    // typing: a save round-trip (debounced PATCH -> query invalidation ->
    // this effect re-running with the server's echoed content) would
    // otherwise reset the doc and drop the cursor mid-keystroke. Once the
    // user blurs (e.g. to switch to the chat), the next external change syncs
    // normally.
    if (editable && editor.isFocused) {
      return;
    }

    // Skip re-parsing when the editor already shows this exact content — this
    // is what a save round-trip looks like (our own debounced PATCH echoed
    // back unchanged) and re-setting it would still reset the selection even
    // when unfocused between keystrokes. Still record the synced markdown so a
    // trailing onUpdate echoing this content is recognized as "not a user edit".
    if (editable && editor.getMarkdown().trim() === combinedContent.trim()) {
      lastSyncedMarkdownRef.current = editor.getMarkdown();
      return;
    }

    // Preserve the reader's vertical scroll across a programmatic content sync.
    // A save round-trip or a quill-committed preview edit re-runs this effect;
    // setContent/clearContent otherwise snap the doc back to the top mid-read.
    // The scroll container is EditorContent's wrapper (overflow-y-auto), which
    // is the ProseMirror node's parent element. `editor.view` THROWS before the
    // view mounts, so access it defensively — pre-mount there's nothing
    // scrolled to preserve anyway.
    let scroller: HTMLElement | null = null;
    try {
      scroller = editor.view.dom.parentElement;
    } catch {
      scroller = null;
    }
    const prevScrollTop = scroller?.scrollTop ?? 0;

    if (combinedContent && combinedContent.trim() !== '') {
      try {
        const html = markdownParser.parse(combinedContent) as string;
        // emitUpdate:false — this is a programmatic sync, not a user edit, so
        // it must not fire onUpdate (which would debounce-save the server's
        // own content straight back and, on any timing hiccup, clobber it).
        editor.commands.setContent(html, { emitUpdate: false });
      } catch (error) {
        // Handle ProseMirror schema validation errors (e.g., invalid list item content)
        // These can occur when markdown contains complex nested structures that don't
        // match ProseMirror's expected node schema
        logger.warn('Failed to parse markdown content, falling back to text', {
          error,
          contentLength: combinedContent.length,
        });
        captureException(error as Error, {
          tags: { module: 'automation-run', action: 'parse_markdown' },
          extra: {
            contentPreview: combinedContent.substring(0, 200),
            contentLength: combinedContent.length,
          },
        });

        // Fallback: Set content as plain text wrapped in a paragraph
        // This ensures the editor still displays something meaningful
        try {
          editor.commands.setContent(
            `<p>${combinedContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`,
            { emitUpdate: false },
          );
        } catch (fallbackError) {
          // If even plain text fails, clear the editor
          logger.error('Failed to set fallback content', { fallbackError });
          editor.commands.clearContent(false);
        }
      }
    } else {
      // Clear content if no content. emitUpdate:false — same as setContent above;
      // an empty initial load (query still in flight) must not fire onUpdate and
      // debounce-save an empty preview.md over the seeded artifact.
      editor.commands.clearContent(false);
    }
    // Restore the scroll position the programmatic write just reset. Set it
    // synchronously, then once more after paint for when the new content's
    // layout height hasn't settled yet. The browser clamps to scrollHeight, so
    // a now-shorter doc simply lands at the bottom.
    let scrollRaf = 0;
    if (scroller && prevScrollTop > 0) {
      scroller.scrollTop = prevScrollTop;
      scrollRaf = requestAnimationFrame(() => {
        scroller.scrollTop = prevScrollTop;
      });
    }
    // Record the editor's normalized markdown after the programmatic write so
    // the `onUpdate` handler can recognize (and ignore) this exact content —
    // including the update event the commands above just emitted — as "not a
    // user edit". Reading it back (rather than storing `combinedContent`)
    // accounts for TipTap's markdown normalization.
    lastSyncedMarkdownRef.current = editor.getMarkdown();

    // Cancel the pending post-paint scroll restore if this effect re-runs or the
    // component unmounts before the next frame, so the RAF never fires on a
    // detached node.
    return () => {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
    };
  }, [editor, combinedContent, editable]);

  return editor;
}
